import { useCallback, useEffect, useContext } from "react";

import MedleySizeOptions from "@components/store/product-detail/shared/medley-size-options";
import PropertiesAccordionList from "@components/store/product-detail/shared/properties-accordion-list";
import QuantitySelector from "@components/store/product-detail/shared/quantity-selector";
import VendorHolidayMessage from "@components/store/product-detail/shared/vendor-holiday-message";
import ProductNotAvailableMessage from "@components/store/product-detail/shared/product-not-available-message";
import ProductInfoSection from "@components/store/product-detail/shared/product-info-section";
import ProductDescriptionAccordion from "@components/store/product-detail/shared/accordion/product-description";
import { getProductType, getProductCategory } from "@helpers/products/types";

import useShowPrefrenceRestrictionModal from "@hooks/product-detail/use-show-prefrence-restriction-modal";
import useGetCartAddedVariant from "@hooks/product-detail/use-get-cart-added-variant";
import useStoreCurrencyConverter from "@hooks/currency/use-store-currency-converter";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import { useProductInfoContext } from "@contexts/product-info-context";

import styles from "./main.module.scss";

const ThemeBoxDetails = ({
  product,
  productQuantity,
  setProductQuantity,
  variantSelected,
  setSelectedVariant,
  renderProductDetailHeader,
  medleyProductOptions,
  canBuyProduct,
}) => {
  const { isMobileDevice } = useContext(CurrentDeviceContext);
  const { name, dietaryRestrictions, ingredients, vendorByCountry, intent } = product;

  const {
    hideVariantSelectionSection,
    showAddToBox,
    isViewOnlyMode,
    renderCatalogModeFooterLinks,
    renderCustomVariantSelectionSection,
    onSuccessCallback,
  } = useProductInfoContext();

  const { convertPriceInStoreCurrency } = useStoreCurrencyConverter();

  const cartAddedVariant = useGetCartAddedVariant({ intent });

  const { renderRestrictionsModal, renderAddToBagBtn } = useShowPrefrenceRestrictionModal({
    product,
    selectedVariant: variantSelected,
    isBYOProduct: showAddToBox,
  });

  const renderQuantitySelector = () => (
    <div className={styles.qtyColLeft}>
      <span className={styles.qtyWrapper}>Quantity</span>
      <QuantitySelector
        quantity={productQuantity}
        setQuantity={setProductQuantity}
        className="defaultStyle"
        disabled={!canBuyProduct}
        eventLocationAnalytics={{
          track: true,
          eventProperties: {
            product_id: product.id,
            product_type: getProductType(product),
            category: getProductCategory(product),
          },
        }}
      />
    </div>
  );
  const setThemeBoxOption = useCallback(
    (data) => {
      setSelectedVariant(data);
    },
    [setSelectedVariant],
  );
  //set default variant
  useEffect(() => {
    let medleyProductOptionsLastIndex = medleyProductOptions.length - 1;
    if (cartAddedVariant) {
      const { price: cartPrice, currency: cartCurrency } = cartAddedVariant;
      medleyProductOptionsLastIndex = medleyProductOptions.findIndex(
        ({ priceIn }) =>
          Number.parseFloat(priceIn) ===
          Number.parseFloat(convertPriceInStoreCurrency(cartPrice, cartCurrency)), //This has been added because cart item is in order currency and comparison here is happening in store currency.
      );
      setProductQuantity(cartAddedVariant.quantity);
    }
    const defaultMedleyProductOption = medleyProductOptions[medleyProductOptionsLastIndex];

    setThemeBoxOption(defaultMedleyProductOption);
  }, [medleyProductOptions, setThemeBoxOption, cartAddedVariant]);

  const handleQuantity = (newQuantity) => {
    setProductQuantity(newQuantity);
  };

  const updatedProduct = { ...product, priceIn: variantSelected?.priceIn };

  const addToBagProps = {
    product: updatedProduct,
    quantity: productQuantity,
    variant: variantSelected,
    btnParentClass: styles.bagBtnBlock,
    showAddToBox,
    onSuccessCallback,
    handleQuantity,
    isDisabled: !canBuyProduct,
  };

  return (
    <div className={`${styles.boxContainerFluid} boxContainerFluid`}>
      {!isMobileDevice && renderProductDetailHeader()}

      <VendorHolidayMessage vendor={vendorByCountry} />
      {!hideVariantSelectionSection && (
        <MedleySizeOptions
          title="Box Size"
          options={medleyProductOptions}
          selectedData={variantSelected}
          handleChange={setThemeBoxOption}
          productId={product.id}
          product={product}
          disabled={!canBuyProduct}
        />
      )}
      <ProductNotAvailableMessage canBuyProduct={canBuyProduct} name={name} />

      {!(hideVariantSelectionSection || isViewOnlyMode) && (
        <div className={`${styles.qtyWithBagBtnRow} stickyDemoMode`}>
          {renderQuantitySelector()}
          {renderAddToBagBtn(addToBagProps)}
        </div>
      )}
      {hideVariantSelectionSection && renderCustomVariantSelectionSection()}
      <ProductDescriptionAccordion product={product} variantSelected={variantSelected} />
      <PropertiesAccordionList
        restrictions={dietaryRestrictions}
        ingredients={ingredients}
        instructions={product?.productDetail?.orderInstructions}
        accordionClass={styles.accordionBox}
        productId={product.id}
      />
      <ProductInfoSection product={product} />
      {!hideVariantSelectionSection &&
        renderRestrictionsModal({
          product: updatedProduct,
          quantity: productQuantity,
          variant: variantSelected,
        })}
      {renderCatalogModeFooterLinks()}
    </div>
  );
};

export default ThemeBoxDetails;
